import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'http://ccbcht.com/' },
      { '@type': 'ListItem', position: 2, name: '关于我们', item: 'http://ccbcht.com/about' },
    ],
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '韩漫在线',
    alternateName: '韩漫网',
    url: 'http://ccbcht.com/',
    description: '韩漫在线提供免费韩国漫画大全，支持全集下拉式阅读，让您在线畅读各种韩漫。',
    foundingDate: '2024',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: 'hello@ccbcht.com',
      availableLanguage: ['Chinese'],
    },
  };

  const webPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于我们 - 韩漫在线',
    description: '韩漫网品牌介绍、口碑展示和联系方式。韩漫在线致力于为中文读者提供最优质的免费韩国漫画阅读体验。',
    url: 'http://ccbcht.com/about',
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('loading');
    setFormError('');

    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = formData.get('website_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setFormStatus('success');
      form.reset();
      return;
    }

    try {
      const response = await fetch('http://ccbcht.com/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const errMsg = parsed?.meta?.message || parsed?.meta?.detail || parsed?.message || responseText || '提交失败，请稍后再试';
        if (errMsg.toLowerCase().includes('spam') || errMsg.includes('form data is spam')) {
          setFormError(errMsg);
          setFormStatus('error');
        } else {
          setFormError(errMsg);
          setFormStatus('error');
        }
      }
    } catch {
      setFormError('网络异常，请检查您的网络连接后重试');
      setFormStatus('error');
    }
  };

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(orgSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(webPageSchema) }} />
      <Navbar />
      <main className="pt-16">
        {/* Brand Intro */}
        <section className="bg-background-100">
          <div className="px-4 md:px-6 py-16 md:py-20">
            <div className="max-w-[1200px] mx-auto text-center">
              <h1 className="text-3xl md:text-5xl font-bold text-foreground-900 font-heading">
                关于<span className="text-primary-600">韩漫在线</span>
              </h1>
              <p className="mt-4 text-sm md:text-base text-foreground-600 max-w-2xl mx-auto leading-relaxed">
                韩漫在线（韩漫网）成立于2024年，是一个专注于免费韩国漫画在线阅读的中文平台。我们致力于为广大中文读者提供高质量的<strong>韩漫全集</strong>，支持便捷的<strong>下拉式阅读</strong>方式。
              </p>
            </div>
          </div>
        </section>

        {/* Brand Story */}
        <section className="py-16 md:py-20">
          <div className="px-4 md:px-6">
            <div className="max-w-[1200px] mx-auto">
              <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-16">
                <div className="flex-1">
                  <span className="inline-block px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium mb-4">
                    品牌故事
                  </span>
                  <h2 className="text-2xl md:text-4xl font-bold text-foreground-900 font-heading leading-tight">
                    让每一位读者都能
                    <span className="text-primary-600">畅享韩漫</span>
                  </h2>
                  <div className="mt-6 space-y-4 text-sm text-foreground-600 leading-relaxed">
                    <p>
                      我们是一个由漫画爱好者和技术专家组成的团队。创始团队自身就是资深的<strong>韩国漫画</strong>读者，深知中文读者在寻找高质量韩漫资源时面临的困难——语言障碍、资源分散、阅读体验不佳等问题。
                    </p>
                    <p>
                      为此，我们打造了<strong>韩漫在线</strong>平台，汇集了超过<strong>8，500部</strong>韩国漫画作品，覆盖热血、恋爱、奇幻、武侠等全部分类。无论你喜欢什么样的类型，都能在这里找到心仪的作品。
                    </p>
                    <p>
                      我们独创的<strong>下拉式阅读</strong>模式，完美适配手机、平板和电脑，让阅读体验更加流畅自然。无需翻页，无需等待加载，只需轻轻滑动，即可沉浸在精彩的漫画世界中。
                    </p>
                  </div>
                </div>
                <div className="flex-1">
                  <div className="rounded-xl overflow-hidden border border-background-200/70">
                    <img
                      src="https://readdy.ai/api/search-image?query=A%20modern%20minimalist%20office%20team%20collaborating%20around%20a%20table%20with%20laptops%20and%20design%20materials%2C%20warm%20natural%20lighting%2C%20creative%20workspace%20vibe%2C%20clean%20professional%20photography%2C%20soft%20shadows%2C%20casual%20but%20focused%20atmosphere%2C%20neutral%20warm%20tones&width=700&height=500&seq=about-brand-01&orientation=landscape"
                      alt="韩漫在线团队"
                      className="w-full h-auto"
                      title="韩漫在线 - 团队风采"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="py-16 md:py-20 bg-background-100">
          <div className="px-4 md:px-6">
            <div className="max-w-[1200px] mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-2xl md:text-4xl font-bold text-foreground-900 font-heading">我们的成绩</h2>
                <p className="mt-3 text-sm text-foreground-600">数据说明一切，感谢每一位读者的支持</p>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                {[
                  { num: '8,500+', label: '漫画资源', icon: 'ri-book-open-line', desc: '涵盖全部分类' },
                  { num: '320万', label: '活跃读者', icon: 'ri-user-line', desc: '遍布全国各地' },
                  { num: '9.2', label: '平均评分', icon: 'ri-star-line', desc: '品质值得信赖' },
                  { num: '24h', label: '极速更新', icon: 'ri-flashlight-line', desc: '同步韩国最新' },
                ].map((stat) => (
                  <div key={stat.label} className="text-center p-6 rounded-xl bg-background-50 border border-background-200/70">
                    <div className="w-12 h-12 rounded-lg bg-primary-100 flex items-center justify-center mx-auto mb-4">
                      <i className={`${stat.icon} text-xl text-primary-600`}></i>
                    </div>
                    <div className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading">{stat.num}</div>
                    <div className="text-sm font-semibold text-foreground-700 mt-1">{stat.label}</div>
                    <div className="text-xs text-foreground-500 mt-0.5">{stat.desc}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Reviews */}
        <section className="py-16 md:py-20">
          <div className="px-4 md:px-6">
            <div className="max-w-[1200px] mx-auto">
              <div className="text-center mb-12">
                <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium mb-4">
                  用户口碑
                </span>
                <h2 className="text-2xl md:text-4xl font-bold text-foreground-900 font-heading">读者怎么说</h2>
                <p className="mt-3 text-sm text-foreground-600">来自真实用户的评价</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {[
                  { name: '漫画迷小王', avatar: 'W', text: '找了好久韩漫资源，终于找到一个这么全的网站！下拉式阅读真的太方便了，在手机上体验超级好，现在每天都要刷一会。', rating: 5 },
                  { name: '宅女木木', avatar: 'M', text: '女神降临全系列都在这里看完了！画面清晰，更新也快，而且完全免费，真的是漫画爱好者的福音。', rating: 5 },
                  { name: '二次元老司机', avatar: 'L', text: '分类很全，从热血到恋爱都有。推荐系统也很准，发现了好几部冷门好作品。希望平台越做越好！', rating: 4 },
                  { name: '追更达人', avatar: 'Z', text: '更新速度真的快，韩网刚出这边就有了。支持各种设备，电脑看完手机接着看，无缝衔接。', rating: 5 },
                  { name: '上班摸鱼选手', avatar: 'S', text: '不用注册就能看全部漫画这点很赞！界面干净没有广告干扰，阅读体验一流。', rating: 5 },
                  { name: '小李爱漫画', avatar: 'X', text: '从大学就开始看韩漫了，韩漫在线是我用过最好的中文韩漫平台。资源丰富，更新及时，强烈推荐！', rating: 5 },
                ].map((review, idx) => (
                  <div key={idx} className="p-6 rounded-xl bg-background-50 border border-background-200/70">
                    <div className="flex items-center gap-1 mb-3">
                      {Array.from({ length: 5 }, (_, i) => (
                        <i key={i} className={`text-sm ${i < review.rating ? 'ri-star-fill text-accent-500' : 'ri-star-line text-foreground-300'}`}></i>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-600 leading-relaxed mb-4">{review.text}</p>
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-primary-100 flex items-center justify-center text-sm font-semibold text-primary-700">
                        {review.avatar}
                      </div>
                      <span className="text-sm font-semibold text-foreground-900">{review.name}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Contact */}
        <section id="contact" className="py-16 md:py-20 bg-background-100">
          <div className="px-4 md:px-6">
            <div className="max-w-[1200px] mx-auto">
              <div className="flex flex-col lg:flex-row gap-12 lg:gap-16">
                <div className="flex-1">
                  <span className="inline-block px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium mb-4">
                    联系我们
                  </span>
                  <h2 className="text-2xl md:text-4xl font-bold text-foreground-900 font-heading leading-tight">
                    有任何问题
                    <br />
                    随时<strong>联系我们</strong>
                  </h2>
                  <p className="mt-4 text-sm text-foreground-600 leading-relaxed">
                    无论是功能建议、内容反馈还是商业合作，都欢迎通过下方表单或以下方式联系我们。我们会尽快回复您的消息。
                  </p>
                  <div className="mt-8 space-y-5">
                    <div className="flex items-center gap-4">
                      <div className="w-11 h-11 rounded-lg bg-primary-100 flex items-center justify-center">
                        <i className="ri-mail-line text-lg text-primary-600"></i>
                      </div>
                      <div>
                        <p className="text-xs text-foreground-500">邮箱</p>
                        <p className="text-sm font-semibold text-foreground-900">hello@ccbcht.com</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="w-11 h-11 rounded-lg bg-accent-100 flex items-center justify-center">
                        <i className="ri-wechat-line text-lg text-accent-600"></i>
                      </div>
                      <div>
                        <p className="text-xs text-foreground-500">微信公众号</p>
                        <p className="text-sm font-semibold text-foreground-900">韩漫在线</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="w-11 h-11 rounded-lg bg-secondary-100 flex items-center justify-center">
                        <i className="ri-weibo-line text-lg text-secondary-600"></i>
                      </div>
                      <div>
                        <p className="text-xs text-foreground-500">微博</p>
                        <p className="text-sm font-semibold text-foreground-900">@韩漫在线官方</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex-1">
                  <div className="p-6 md:p-8 rounded-xl bg-background-50 border border-background-200/70">
                    <h3 className="text-lg font-bold text-foreground-900 mb-6 font-heading">发送消息</h3>

                    {formStatus === 'success' && (
                      <div className="mb-6 p-4 rounded-lg bg-primary-50 border border-primary-200">
                        <p className="text-sm text-primary-700 font-medium">
                          <i className="ri-check-line mr-1"></i>
                          消息已发送成功！我们会尽快回复您。
                        </p>
                      </div>
                    )}

                    {formStatus === 'error' && (
                      <div className="mb-6 p-4 rounded-lg bg-accent-50 border border-accent-200">
                        <p className="text-sm text-accent-700">{formError}</p>
                      </div>
                    )}

                    <form id="about-contact-form" onSubmit={handleSubmit} className="space-y-4">
                      <input
                        type="text"
                        name="website_alt"
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                        readOnly
                      />
                      <div>
                        <label htmlFor="contact-name" className="block text-xs font-medium text-foreground-700 mb-1.5">姓名</label>
                        <input
                          id="contact-name"
                          name="name"
                          type="text"
                          required
                          className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                          placeholder="请输入您的姓名"
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-email" className="block text-xs font-medium text-foreground-700 mb-1.5">邮箱</label>
                        <input
                          id="contact-email"
                          name="email"
                          type="email"
                          required
                          className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                          placeholder="请输入您的邮箱"
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-subject" className="block text-xs font-medium text-foreground-700 mb-1.5">主题</label>
                        <input
                          id="contact-subject"
                          name="subject"
                          type="text"
                          required
                          className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                          placeholder="请输入消息主题"
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-message" className="block text-xs font-medium text-foreground-700 mb-1.5">消息内容</label>
                        <textarea
                          id="contact-message"
                          name="message"
                          required
                          maxLength={500}
                          rows={4}
                          className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                          placeholder="请输入您的消息（最多500字）"
                        ></textarea>
                      </div>
                      <button
                        type="submit"
                        disabled={formStatus === 'loading'}
                        className="w-full px-6 py-3 bg-primary-500 text-background-50 text-sm font-semibold rounded-lg hover:bg-primary-600 transition-all disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
                      >
                        {formStatus === 'loading' ? (
                          <>
                            <i className="ri-loader-4-line animate-spin mr-2"></i>
                            发送中...
                          </>
                        ) : (
                          <>
                            <i className="ri-send-plane-line mr-2"></i>
                            发送消息
                          </>
                        )}
                      </button>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}